import React from 'react'
import find from 'lodash/find'
import isInteger from 'lodash/isInteger'
import map from 'lodash/map'
import toNumber from 'lodash/toNumber'
import { Stack } from 'react-bootstrap'
import { useFormikContext } from 'formik'
import type { PivotLayout, PivotLayoutField } from '@ztechapps/common.contract'
import type { PivotData } from '../pivot/types'
import type { PivotFilterFormValues } from '../pivot/pivotFilterForm.utils'
import {
  PivotFieldTypeEnum,
  PivotFilterOperatorEnum,
  PivotValueFilterModeEnum,
  PivotValueFilterScopeEnum,
} from '@ztechapps/common.contract'
import { isPivotTopOrBottomOperator } from '@ztechapps/common.utils'
import { Field } from '@ztechapps/webui.form'
import { NumberInput, RadioInputGroup } from '@ztechapps/webui.input'
import { ShowIf } from '@ztechapps/webui.util'
import { findPivotRankParent, listPivotRankMeasures, toPivotMeasureKey } from '../pivot/pivotFilterForm.utils'
import { PIVOT_VALUE_OPERATOR_OPTIONS } from '../pivot/pivotFilterOperator.utils'
import { toPivotFieldLabel } from '../pivot/toPivotFieldLabel'
import { PivotFilterSectionEnum, PivotFilterSelect, PivotOperandFields } from './PivotFilterFields'

const MODE_OPTIONS = [
  { label: 'Items', value: PivotValueFilterModeEnum.items },
  { label: 'Percent', value: PivotValueFilterModeEnum.percent },
]

/** Props for the Value pane of the filter dialog. */
export type PivotFilterValuePaneProps = {
  data: PivotData
  field: PivotLayoutField
  layout: Pick<PivotLayout, 'rows' | 'columns' | 'measures' | 'filters'>
}

/**
 * The Value kind — a value to rank the field's members by, then Top or Bottom N (an item count or a percentage,
 * across the whole axis or inside each member of the field before it), or one of the eight comparisons.
 */
export const PivotFilterValuePane = ({ data, field, layout }: PivotFilterValuePaneProps) => {
  const { values } = useFormikContext<PivotFilterFormValues>()
  const { operator, mode, count, scope } = values.value
  const measureOptions = map(listPivotRankMeasures(layout), (measure) => ({
    value: toPivotMeasureKey(measure),
    label: `${measure.field} — ${measure.aggregation}`,
  }))
  const isTopBottom = isPivotTopOrBottomOperator(operator)

  const labelOf = (item: PivotLayoutField) =>
    toPivotFieldLabel(
      find(data.fields, (candidate) => candidate.name === item.field),
      item.field,
      item.dateGroup
    )
  const label = labelOf(field)
  const parent = findPivotRankParent(layout, field)
  const hasParent = parent !== undefined
  const shownScope = hasParent ? scope : PivotValueFilterScopeEnum.overall
  const total = toNumber(count)
  // the entered count, else N while the field is blank or not a whole number
  const shownCount = isInteger(total) && total >= 1 ? total : 'N'
  const direction = operator === PivotFilterOperatorEnum.top ? 'Top' : 'Bottom'
  const rank = operator === PivotFilterOperatorEnum.top ? 'highest' : 'lowest'
  const hint =
    mode === PivotValueFilterModeEnum.percent
      ? `${direction} ${shownCount}% keeps the ${rank}-ranked ${shownCount}% of members`
      : `${direction} ${shownCount} keeps the ${shownCount} ${rank}-ranked members`
  // "Within each" needs an outer field to rank inside, so without one the option is not offered
  const scopeOptions = [
    { label: 'Overall', value: PivotValueFilterScopeEnum.overall },
    ...(hasParent ? [{ label: `Within each ${labelOf(parent)}`, value: PivotValueFilterScopeEnum.perParent }] : []),
  ]
  const scopeHint = hasParent
    ? `Ranks the ${label} members inside each ${labelOf(parent)} member, not across the whole axis.`
    : `${label} is the outermost field, so there is no outer field to rank within.`

  return (
    <Stack gap={2}>
      <Field
        name="value.measure"
        label="Measure"
        component={PivotFilterSelect}
        options={measureOptions}
        onChange
        help={`What each ${label} member is ranked or compared by.`}
      />
      <Field
        name="value.operator"
        label="Operator"
        component={PivotFilterSelect}
        options={PIVOT_VALUE_OPERATOR_OPTIONS}
        onChange
      />
      <ShowIf condition={!isTopBottom}>
        <PivotOperandFields
          section={PivotFilterSectionEnum.value}
          operator={operator}
          valueType={PivotFieldTypeEnum.Number}
        />
      </ShowIf>
      <ShowIf condition={isTopBottom}>
        <Field name="value.mode" component={RadioInputGroup} options={MODE_OPTIONS} inline />
        <Field
          name="value.count"
          label={mode === PivotValueFilterModeEnum.percent ? 'How many members (%)' : 'How many members'}
          component={NumberInput}
          min={1}
          max={mode === PivotValueFilterModeEnum.percent ? 100 : undefined}
          help={hint}
          showHelpAsInline
        />
        <Field
          name="value.scope"
          label="Scope"
          component={RadioInputGroup}
          options={scopeOptions}
          value={shownScope}
          inline
          help={scopeHint}
          showHelpAsInline
        />
      </ShowIf>
    </Stack>
  )
}
